/**
 * Utility functions for generating public image URLs
 */

const BASE_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://www.makemybrandlabs.com';

/**
 * Generates a public absolute URL for an image
 * @param imagePath - Relative path (e.g., /uploads/blogs/image.jpg) or absolute URL
 * @returns Absolute URL to the image
 */
export function getPublicImageUrl(imagePath: string | undefined | null): string {
  if (!imagePath) return '';

  // If already absolute URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // If we're running in the browser, prefer the current origin so images
  // work correctly in local/staging environments even when BASE_URL points
  // to production.
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;

    // If relative path, prepend current origin
    if (imagePath.startsWith('/')) {
      return `${origin}${imagePath}`;
    }

    // If no leading slash, add it
    return `${origin}/${imagePath}`;
  }

  // Server-side fallback: use configured BASE_URL
  // Note: next.config.ts rewrites /uploads/* to /api/uploads/*, so both work
  if (imagePath.startsWith('/')) {
    return `${BASE_URL}${imagePath}`;
  }

  return `${BASE_URL}/${imagePath}`;
}

/**
 * Validates if an image URL is accessible
 * @param imageUrl - Absolute image URL
 * @returns Promise<boolean> - true if image is accessible
 */
export async function validateImageUrl(imageUrl: string): Promise<boolean> {
  if (!imageUrl) return false;

  try {
    await fetch(imageUrl, { method: 'HEAD', mode: 'no-cors' });
    return true; // If no-cors, we can't check status, assume it works
  } catch {
    // Try with a simple GET request
    try {
      const img = new Image();
      return new Promise((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = imageUrl;
      });
    } catch {
      return false;
    }
  }
}

/**
 * Generates a public absolute URL for an uploaded image
 * @param filename - Image filename (e.g., image.jpg)
 * @param uploadType - Type of upload (blogs, testimonials, service-meta)
 * @returns Absolute URL to the image
 */
export function getUploadedImageUrl(
  filename: string | undefined | null,
  uploadType: 'blogs' | 'testimonials' | 'service-meta' = 'blogs'
): string {
  if (!filename) return '';

  // Remove leading slash if present
  const cleanFilename = filename.startsWith('/') ? filename.slice(1) : filename;

  return `${BASE_URL}/uploads/${uploadType}/${cleanFilename}`;
}

/**
 * Extracts filename from an image URL
 * @param url - Image URL (absolute or relative)
 * @returns Filename or empty string
 */
export function extractFilenameFromUrl(url: string): string {
  if (!url) return '';

  // Handle absolute URLs
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    return pathParts[pathParts.length - 1] || '';
  } catch {
    // Handle relative URLs
    const pathParts = url.split('/');
    return pathParts[pathParts.length - 1] || '';
  }
}

